import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// ============================================
// DATABASE SCHEMA
// ============================================

interface FeedbackQueueItem {
  id: string;
  qrCode: string;
  rating?: number;
  comment?: string;
  createdAt: Date;
  retryCount: number;
  // Image data stored as ArrayBuffer for offline persistence
  imageData?: ArrayBuffer;
  imageMimeType?: string;
  imageFileName?: string;
}

interface StatusReportQueueItem {
  id: string;
  qrCode: string;
  statusItemId: string;
  newStatus: string;
  comment?: string;
  rating?: number;
  createdAt: Date;
  retryCount: number;
}

interface FeedbackQueueDB extends DBSchema {
  feedbackQueue: {
    key: string;
    value: FeedbackQueueItem;
    indexes: {
      'by-created': Date;
    };
  };
  statusReportQueue: {
    key: string;
    value: StatusReportQueueItem;
    indexes: {
      'by-created': Date;
    };
  };
}

// ============================================
// DATABASE INITIALIZATION
// ============================================

const DB_NAME = 'tendedloop-visitor';
const DB_VERSION = 2; // Bumped for new statusReportQueue store

let dbPromise: Promise<IDBPDatabase<FeedbackQueueDB>> | null = null;

function getDB(): Promise<IDBPDatabase<FeedbackQueueDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FeedbackQueueDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Create feedback queue store (version 1)
        if (oldVersion < 1) {
          const feedbackStore = db.createObjectStore('feedbackQueue', {
            keyPath: 'id',
          });
          feedbackStore.createIndex('by-created', 'createdAt');
        }

        // Create status report queue store (version 2)
        if (oldVersion < 2) {
          const statusStore = db.createObjectStore('statusReportQueue', {
            keyPath: 'id',
          });
          statusStore.createIndex('by-created', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

// ============================================
// QUEUE OPERATIONS
// ============================================

/**
 * Add feedback to the offline queue
 */
export async function queueFeedback(data: {
  qrCode: string;
  rating?: number;
  comment?: string;
  image?: File;
}): Promise<string> {
  const db = await getDB();

  // Convert image file to ArrayBuffer for IndexedDB storage
  let imageData: ArrayBuffer | undefined;
  let imageMimeType: string | undefined;
  let imageFileName: string | undefined;

  if (data.image) {
    imageData = await data.image.arrayBuffer();
    imageMimeType = data.image.type;
    imageFileName = data.image.name;
  }

  const item: FeedbackQueueItem = {
    id: `feedback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    qrCode: data.qrCode,
    rating: data.rating,
    comment: data.comment,
    imageData,
    imageMimeType,
    imageFileName,
    createdAt: new Date(),
    retryCount: 0,
  };

  await db.add('feedbackQueue', item);
  return item.id;
}

/**
 * Get all queued feedback items
 */
export async function getQueuedFeedback(): Promise<FeedbackQueueItem[]> {
  const db = await getDB();
  return db.getAllFromIndex('feedbackQueue', 'by-created');
}

/**
 * Get count of queued items
 */
export async function getQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count('feedbackQueue');
}

/**
 * Remove feedback from queue (after successful submission)
 */
export async function removeFromQueue(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('feedbackQueue', id);
}

/**
 * Update retry count for a queued item
 */
export async function incrementRetryCount(id: string): Promise<void> {
  const db = await getDB();
  const item = await db.get('feedbackQueue', id);
  if (item) {
    item.retryCount += 1;
    await db.put('feedbackQueue', item);
  }
}

/**
 * Clear all queued feedback (use with caution)
 */
export async function clearQueue(): Promise<void> {
  const db = await getDB();
  await db.clear('feedbackQueue');
}

// ============================================
// STATUS REPORT QUEUE OPERATIONS
// ============================================

/**
 * Add status report to the offline queue
 */
export async function queueStatusReport(data: {
  qrCode: string;
  statusItemId: string;
  newStatus: string;
  comment?: string;
  rating?: number;
}): Promise<string> {
  const db = await getDB();

  const item: StatusReportQueueItem = {
    id: `status-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    ...data,
    createdAt: new Date(),
    retryCount: 0,
  };

  await db.add('statusReportQueue', item);
  return item.id;
}

/**
 * Get all queued status reports
 */
export async function getQueuedStatusReports(): Promise<StatusReportQueueItem[]> {
  const db = await getDB();
  return db.getAllFromIndex('statusReportQueue', 'by-created');
}

/**
 * Get count of queued status reports
 */
export async function getStatusReportQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count('statusReportQueue');
}

/**
 * Remove status report from queue (after successful submission)
 */
export async function removeStatusReportFromQueue(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('statusReportQueue', id);
}

/**
 * Update retry count for a queued status report
 */
export async function incrementStatusReportRetryCount(id: string): Promise<void> {
  const db = await getDB();
  const item = await db.get('statusReportQueue', id);
  if (item) {
    item.retryCount += 1;
    await db.put('statusReportQueue', item);
  }
}

/**
 * Clear all queued status reports (use with caution)
 */
export async function clearStatusReportQueue(): Promise<void> {
  const db = await getDB();
  await db.clear('statusReportQueue');
}

// ============================================
// SYNC SERVICE
// ============================================

const MAX_RETRIES = 3;

export interface SyncResult {
  success: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
}

/**
 * Attempt to sync all queued feedback to the server
 */
export async function syncQueuedFeedback(apiUrl: string): Promise<SyncResult> {
  const items = await getQueuedFeedback();

  const result: SyncResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const item of items) {
    // Skip items that have exceeded retry limit
    if (item.retryCount >= MAX_RETRIES) {
      result.failed += 1;
      result.errors.push({
        id: item.id,
        error: 'Max retries exceeded',
      });
      continue;
    }

    try {
      // Upload image first if there is one
      let imageKey: string | undefined;
      let imageMimeType: string | undefined;
      let imageFileSize: number | undefined;

      if (item.imageData && item.imageMimeType) {
        try {
          // Request presigned upload URL
          const uploadUrlResponse = await fetch(`${apiUrl}/api/feedback/upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              qrCode: item.qrCode,
              mimeType: item.imageMimeType,
              fileSize: item.imageData.byteLength,
            }),
          });

          const uploadUrlData = await uploadUrlResponse.json();

          if (uploadUrlResponse.ok && uploadUrlData.success) {
            const { uploadUrl, imageKey: key } = uploadUrlData.data;

            // Upload the image directly to R2
            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              body: item.imageData,
              headers: {
                'Content-Type': item.imageMimeType,
              },
            });

            if (uploadResponse.ok) {
              imageKey = key;
              imageMimeType = item.imageMimeType;
              imageFileSize = item.imageData.byteLength;
            }
          }
        } catch (imgError) {
          console.error('Failed to upload queued image:', imgError);
          // Continue without the image
        }
      }

      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qrCode: item.qrCode,
          rating: item.rating,
          comment: item.comment,
          imageKey,
          imageMimeType,
          imageFileSize,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await removeFromQueue(item.id);
        result.success += 1;
      } else {
        await incrementRetryCount(item.id);
        result.failed += 1;
        result.errors.push({
          id: item.id,
          error: data.error || 'Submission failed',
        });
      }
    } catch (error) {
      await incrementRetryCount(item.id);
      result.failed += 1;
      result.errors.push({
        id: item.id,
        error: error instanceof Error ? error.message : 'Network error',
      });
    }
  }

  return result;
}

/**
 * Attempt to sync all queued status reports to the server
 */
export async function syncQueuedStatusReports(apiUrl: string): Promise<SyncResult> {
  const items = await getQueuedStatusReports();

  const result: SyncResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const item of items) {
    // Skip items that have exceeded retry limit
    if (item.retryCount >= MAX_RETRIES) {
      result.failed += 1;
      result.errors.push({
        id: item.id,
        error: 'Max retries exceeded',
      });
      continue;
    }

    try {
      const response = await fetch(`${apiUrl}/api/status/${item.qrCode}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          statusItemId: item.statusItemId,
          newStatus: item.newStatus,
          comment: item.comment,
          rating: item.rating,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await removeStatusReportFromQueue(item.id);
        result.success += 1;
      } else {
        await incrementStatusReportRetryCount(item.id);
        result.failed += 1;
        result.errors.push({
          id: item.id,
          error: data.error || 'Submission failed',
        });
      }
    } catch (error) {
      await incrementStatusReportRetryCount(item.id);
      result.failed += 1;
      result.errors.push({
        id: item.id,
        error: error instanceof Error ? error.message : 'Network error',
      });
    }
  }

  return result;
}

// ============================================
// ONLINE/OFFLINE DETECTION
// ============================================

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Register sync handler for when coming back online
 * Syncs both feedback and status report queues
 */
export function registerOnlineSync(
  apiUrl: string,
  onSync?: (result: SyncResult, type: 'feedback' | 'status') => void
): () => void {
  const handleOnline = async () => {
    // Sync feedback queue
    const feedbackCount = await getQueueCount();
    if (feedbackCount > 0) {
      const feedbackResult = await syncQueuedFeedback(apiUrl);
      onSync?.(feedbackResult, 'feedback');
    }

    // Sync status report queue
    const statusCount = await getStatusReportQueueCount();
    if (statusCount > 0) {
      const statusResult = await syncQueuedStatusReports(apiUrl);
      onSync?.(statusResult, 'status');
    }
  };

  window.addEventListener('online', handleOnline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

/**
 * Get total count of all queued items (feedback + status reports)
 */
export async function getTotalQueueCount(): Promise<number> {
  const feedbackCount = await getQueueCount();
  const statusCount = await getStatusReportQueueCount();
  return feedbackCount + statusCount;
}

// ============================================
// OFFLINE QUEUE OBJECT (for legacy imports)
// ============================================

// Store sync callbacks for notification
const syncCallbacks: Array<(result: SyncResult, type: 'feedback' | 'status') => void> = [];

/**
 * Wrapper object for legacy imports that expect offlineQueue.method() syntax
 */
export const offlineQueue = {
  /**
   * Add feedback to the queue
   */
  add: async (data: {
    qrCode: string;
    rating?: number;
    comment?: string;
    image?: File;
  }): Promise<string> => {
    return queueFeedback(data);
  },

  /**
   * Get count of queued feedback items
   */
  getCount: async (): Promise<number> => {
    return getQueueCount();
  },

  /**
   * Sync all queued feedback
   */
  sync: async (): Promise<SyncResult> => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const result = await syncQueuedFeedback(apiUrl);
    // Notify callbacks
    syncCallbacks.forEach(cb => cb(result, 'feedback'));
    return result;
  },

  /**
   * Register callback for sync completion
   */
  onSync: (callback: (result: SyncResult, type: 'feedback' | 'status') => void): (() => void) => {
    syncCallbacks.push(callback);
    // Return cleanup function
    return () => {
      const index = syncCallbacks.indexOf(callback);
      if (index > -1) {
        syncCallbacks.splice(index, 1);
      }
    };
  },
};
